import React, { ChangeEvent, useState, useEffect, useRef, FormEvent } from 'react';
import styles from './SearchDropdown.module.css';
import SearchBar from './SearchBar';
import { Character } from './ExploreDesktop';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';
import { FaUser, FaBookOpen, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface SearchDropdownProps {
  dropdownCharacters: Character[];
  dropdownScenarios: ScenarioDefinitionListResponse[];
  searchQuery: string;
  activeSearchQuery?: string;
  isLoading: boolean;
  handleSearch: (query: string) => void;
  executeSearch?: () => void;
  clearSearch?: () => void;
  handleCharacterClick: (character: Character) => void;
  handleScenarioClick: (scenario: ScenarioDefinitionListResponse) => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  dropdownCharacters,
  dropdownScenarios,
  searchQuery,
  activeSearchQuery = '',
  isLoading,
  handleSearch,
  executeSearch,
  clearSearch: propsClearSearch,
  handleCharacterClick,
  handleScenarioClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ width: 0 });
  const [expandedCharacters, setExpandedCharacters] = useState(false);
  const [expandedScenarios, setExpandedScenarios] = useState(false);

  // Open dropdown when search query is not empty
  useEffect(() => {
    if (searchQuery.trim() !== '') {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [searchQuery]);

  // Update dropdown position when search bar position changes
  useEffect(() => {
    const updatePosition = () => {
      if (searchBarRef.current) {
        const rect = searchBarRef.current.getBoundingClientRect();
        setDropdownPosition({
          width: rect.width
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset expanded state when closing dropdown
        setExpandedCharacters(false);
        setExpandedScenarios(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearch(event.target.value);
    // Reset expanded state when search query changes
    setExpandedCharacters(false);
    setExpandedScenarios(false);
  };

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (executeSearch) {
      executeSearch();
    }
    setIsOpen(false); // Close dropdown after search execution
  };

  const clearSearchLocal = () => {
    if (propsClearSearch) {
      propsClearSearch();
    } else {
      handleSearch('');
    }
    setIsOpen(false);
    setExpandedCharacters(false);
    setExpandedScenarios(false);
  };

  const handleResultClick = (item: Character | ScenarioDefinitionListResponse) => {
    if ('display_name' in item && 'username' in item) {
      handleCharacterClick(item as Character);
    } else {
      handleScenarioClick(item as ScenarioDefinitionListResponse);
    }
    setIsOpen(false);
    setExpandedCharacters(false);
    setExpandedScenarios(false);
  };

  const toggleExpandCharacters = () => {
    setExpandedCharacters(!expandedCharacters);
  };

  const toggleExpandScenarios = () => {
    setExpandedScenarios(!expandedScenarios);
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Searching...</p>
        </div>
      );
    }
    
    const hasResults = dropdownCharacters.length > 0 || dropdownScenarios.length > 0;
    
    if (!hasResults) {
      return (
        <div className={styles.noResults}>
          <p>No results found</p>
        </div>
      );
    }

    // Determine how many characters to show
    const charactersToShow = expandedCharacters 
      ? dropdownCharacters 
      : dropdownCharacters.slice(0, 3);

    // Determine how many scenarios to show
    const scenariosToShow = expandedScenarios 
      ? dropdownScenarios 
      : dropdownScenarios.slice(0, 3);

    return (
      <div className={styles.resultsList}>
        {/* Characters Section */}
        {dropdownCharacters.length > 0 && (
          <>
            <div className={styles.resultSectionHeader}>Characters</div>
            {charactersToShow.map((character) => (
              <div 
                key={character.id} 
                className={styles.resultItem}
                onClick={() => handleResultClick(character)}
              >
                <div className={styles.resultIcon}>
                  {character.icon ? (
                    <img 
                      src={character.icon} 
                      alt={character.display_name}
                      className={styles.resultImage}
                    />
                  ) : (
                    <FaUser />
                  )}
                </div>
                <div className={styles.resultInfo}>
                  <div className={styles.resultName}>{character.display_name}</div>
                  <div className={styles.resultType}>Character</div>
                </div>
              </div>
            ))}
            {dropdownCharacters.length > 3 && (
              <div 
                className={styles.moreResults}
                onClick={toggleExpandCharacters}
              >
                {expandedCharacters ? (
                  <>
                    <span>Show less</span>
                    <FaChevronUp className={styles.expandIcon} />
                  </>
                ) : (
                  <>
                    <span>{dropdownCharacters.length - 3} more characters...</span>
                    <FaChevronDown className={styles.expandIcon} />
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Scenarios Section */}
        {dropdownScenarios.length > 0 && (
          <>
            <div className={styles.resultSectionHeader}>Scenarios</div>
            {scenariosToShow.map((scenario) => (
              <div 
                key={scenario.id} 
                className={styles.resultItem}
                onClick={() => handleResultClick(scenario)}
              >
                <div className={styles.resultIcon}>
                  {scenario.icon ? (
                    <img 
                      src={scenario.icon} 
                      alt={scenario.display_name}
                      className={styles.resultImage}
                    />
                  ) : (
                    <FaBookOpen />
                  )}
                </div>
                <div className={styles.resultInfo}>
                  <div className={styles.resultName}>{scenario.display_name}</div>
                  <div className={styles.resultType}>Scenario</div>
                </div>
              </div>
            ))}
            {dropdownScenarios.length > 3 && (
              <div 
                className={styles.moreResults}
                onClick={toggleExpandScenarios}
              >
                {expandedScenarios ? (
                  <>
                    <span>Show less</span>
                    <FaChevronUp className={styles.expandIcon} />
                  </>
                ) : (
                  <>
                    <span>{dropdownScenarios.length - 3} more scenarios...</span>
                    <FaChevronDown className={styles.expandIcon} />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.searchContainer} ref={searchBarRef}>
      <div className={styles.searchBarWrapper}>
        <SearchBar 
          value={searchQuery}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          onClear={clearSearchLocal}
          activeSearchQuery={activeSearchQuery}
          className={styles.searchBar}
        />
      </div>
      
      {/* Render dropdown directly under the search bar instead of as a portal */}
      {isOpen && searchQuery.trim() !== '' && (
        <div 
          className={styles.dropdownContainer}
          ref={dropdownRef}
          style={{
            width: `${dropdownPosition.width}px`
          }}
        >
          {renderResults()}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown; 